import { Building2, CheckCircle2, Shield, User } from "lucide-react";

import { Separator } from "js/components/ui/separator";

import { PROPERTY_USE_OPTIONS } from "js/constants/property-use";

import { formatCurrency } from "../../../../utils/utils";
import { StepComponentProps } from "../../Tenants/types";

interface StepComponentPropsWithNavigation extends StepComponentProps {
  onNavigateToStep?: (step: number) => void;
}

export const Step4ReviewContract = ({
  formData,
  isAnimating,
  properties = [],
}: StepComponentPropsWithNavigation) => {
  // Get property details
  const selectedProperty = properties.find((p) => p.id === formData.property);

  // Get property use label
  const propertyUseLabel =
    PROPERTY_USE_OPTIONS.find((option) => option.value === formData.propertyUse)
      ?.label || formData.propertyUse;

  const guarantorTypeLabel =
    formData.guarantorType === "moral" ? "Persona Moral" : "Persona Física";

  return (
    <div
      className={`space-y-6 transition-all duration-500 ${isAnimating ? "opacity-0 translate-x-4" : "opacity-100 translate-x-0"}`}
    >
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center gap-3 pb-2">
          <CheckCircle2 className="w-5 h-5 text-green-600" />
          <h3 className="text-lg font-semibold text-gray-900">
            Resumen de tu Contrato
          </h3>
        </div>
        <p className="text-sm text-gray-500">
          Revisa la información antes de crear el contrato. Podrás completar los
          detalles adicionales en el siguiente paso.
        </p>
      </div>

      {/* Property Information */}
      <div className="border border-gray-200 rounded-lg p-5 space-y-3 bg-white hover:shadow-sm transition-shadow duration-200">
        <div className="flex items-center gap-3">
          <Building2 className="w-5 h-5 text-blue-600" />
          <h4 className="text-base font-semibold text-gray-900">
            Información del Inmueble
          </h4>
        </div>
        <Separator />
        <div className="space-y-2 text-sm">
          {selectedProperty && (
            <>
              <div className="flex justify-between">
                <span className="text-gray-600">Alias:</span>
                <span className="font-medium text-gray-900">
                  {selectedProperty.alias || "N/A"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Dirección:</span>
                <span className="font-medium text-gray-900">
                  {selectedProperty.direccion}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Colonia:</span>
                <span className="font-medium text-gray-900">
                  {selectedProperty.colonia}
                </span>
              </div>
            </>
          )}
          <div className="flex justify-between">
            <span className="text-gray-600">Uso:</span>
            <span className="font-medium text-gray-900">
              {propertyUseLabel}
            </span>
          </div>
          {formData.rentAmount && (
            <div className="flex justify-between">
              <span className="text-gray-600">Renta mensual:</span>
              <span className="font-medium text-gray-900">
                {formatCurrency(parseInt(formData.rentAmount, 10))}
              </span>
            </div>
          )}
          {formData.maintenanceAmount &&
            parseInt(formData.maintenanceAmount, 10) > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">Mantenimiento:</span>
                <span className="font-medium text-gray-900">
                  {formatCurrency(parseInt(formData.maintenanceAmount, 10))}
                </span>
              </div>
            )}
        </div>
      </div>

      {/* Tenant Information */}
      <div className="border border-gray-200 rounded-lg p-5 space-y-3 bg-white hover:shadow-sm transition-shadow duration-200">
        <div className="flex items-center gap-3">
          <User className="w-5 h-5 text-purple-600" />
          <h4 className="text-base font-semibold text-gray-900">
            Información del Inquilino
          </h4>
        </div>
        <Separator />
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Tipo:</span>
            <span className="font-medium text-gray-900">
              {formData.tenantType === "fisica"
                ? "Persona Física"
                : "Persona Moral"}
            </span>
          </div>
          {formData.tenantName && (
            <div className="flex justify-between">
              <span className="text-gray-600">Nombre:</span>
              <span className="font-medium text-gray-900">
                {formData.tenantName}
              </span>
            </div>
          )}
          {formData.tenantEmail && (
            <div className="flex justify-between">
              <span className="text-gray-600">Email:</span>
              <span className="font-medium text-gray-900">
                {formData.tenantEmail}
              </span>
            </div>
          )}
          {formData.tenantPhone && (
            <div className="flex justify-between">
              <span className="text-gray-600">Teléfono:</span>
              <span className="font-medium text-gray-900">
                {formData.tenantPhone}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Guarantor Information */}
      {formData.requiresGuarantor && (
        <div className="border border-gray-200 rounded-lg p-5 space-y-3 bg-white hover:shadow-sm transition-shadow duration-200">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-orange-600" />
            <h4 className="text-base font-semibold text-gray-900">
              Información del Fiador/Obligado Solidario
            </h4>
          </div>
          <Separator />
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Tipo:</span>
              <span className="font-medium text-gray-900">
                {guarantorTypeLabel}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Propiedad en garantía:</span>
              <span className="font-medium text-gray-900">
                {formData.propertyAsGuarantee ? "Sí" : "No"}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Legal Protection Information */}
      <div className="border border-green-200 bg-green-50 rounded-lg p-5 space-y-3">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="w-5 h-5 text-green-600" />
          <h4 className="text-base font-semibold text-gray-900">
            Blindaje Legal Multiprotección
          </h4>
        </div>
        <Separator className="bg-green-200" />
        <div className="space-y-2">
          <ul className="text-sm space-y-1 text-gray-700 list-disc list-inside">
            <li>Incluye: Contrato y Convenio de Justicia Alternativa.</li>
            <li>
              Costo del servicio:{" "}
              <span className="font-semibold">{formatCurrency(4600)}</span>{" "}
              <span className="text-gray-600">
                (a cubrir el día de la firma).
              </span>
            </li>
          </ul>
        </div>
      </div>

      {/* Next Steps Note */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Siguiente paso:</strong> Después de crear el contrato, podrás
          completar los detalles de arrendadores, firmatarios, fechas y
          condiciones de pago.
        </p>
      </div>
    </div>
  );
};
